import {defineStore} from 'pinia'
import {computed, ref} from 'vue'
import type {useContext, useDialogs, useProcesses} from '@hantera/portal-app'
import type useBackend from '@/composition/useBackend'
import type {Principal, RoleListItem, Session} from '../../backend/iam.types'
import {useIamEventsStore} from './iam-events-store'

export const usePrincipalStore = (
  principalId: string,
  services: {
    context: ReturnType<typeof useContext>
    dialogs: ReturnType<typeof useDialogs>
    processes: ReturnType<typeof useProcesses>
    iamService: ReturnType<typeof useBackend>['iam']
  }
) => {
  return defineStore(`principal-${principalId}`, () => {
    // State
    const principal = ref<Principal | null>(null)
    const sessions = ref<Session[]>([])
    const roles = ref<RoleListItem[]>([])
    const isLoading = ref(false)

    // Pending changes (null = no changes)
    const pendingPermissions = ref<string[] | null>(null)
    const pendingRoleIds = ref<string[] | null>(null)

    // Services from parameters
    const { context, dialogs, processes, iamService } = services

    // Permissions
    const canWrite = context.authorize('iam/principals:write')

    // Abort controller for cancellation
    let refreshController: AbortController | undefined

    // Getters
    const displayName = () => {
      if (!principal.value) return ''
      return principal.value.name || principal.value.email
    }

    const roleCount = () => principal.value?.roles.length ?? 0
    const sessionCount = () => sessions.value.length

    const hasPermissionsChanges = computed(() => pendingPermissions.value !== null)
    const hasRolesChanges = computed(() => pendingRoleIds.value !== null)
    const hasUnsavedChanges = computed(() => hasPermissionsChanges.value || hasRolesChanges.value)

    // Actions
    async function refresh() {
      if (!principalId) return

      if (refreshController) {
        refreshController.abort('stale')
      }

      refreshController = new AbortController()
      isLoading.value = true

      try {
        const [principalData, sessionsData, rolesResponse] = await Promise.all([
          iamService.getPrincipal(principalId),
          iamService.getPrincipalSessions(principalId),
          iamService.getRoles({}),
        ])

        console.log('API responses:', {
          principalData,
          sessionsData,
          sessionsType: typeof sessionsData,
          sessionsIsArray: Array.isArray(sessionsData),
          rolesResponse,
        })

        principal.value = principalData
        sessions.value = sessionsData
        roles.value = rolesResponse.roles

        // Debug logging
        console.log('Store updated:', {
          principalId,
          principal: principal.value,
          sessionsCount: sessions.value.length,
          rolesCount: roles.value.length,
        })
      } catch (error: any) {
        if (error !== 'stale') {
          console.error('Failed to load principal details:', error)
          processes.error({ message: 'Failed to load user details' })
        }
      } finally {
        refreshController = undefined
        isLoading.value = false
      }
    }

    async function suspend() {
      if (!principal.value) return

      const confirmed = await dialogs.confirm(
        `Are you sure you want to suspend ${displayName()}?`,
        'Suspend User'
      )

      if (!confirmed) return

      try {
        const updatedPrincipal = await iamService.suspendPrincipal(principalId)
        principal.value = updatedPrincipal
        processes.createProcess({
          description: 'User suspended',
        }).complete()
      } catch (error) {
        console.error('Failed to suspend principal:', error)
        processes.error({ message: 'Failed to suspend user' })
      }
    }

    async function reactivate() {
      if (!principal.value) return

      try {
        const updatedPrincipal = await iamService.reactivatePrincipal(principalId)
        principal.value = updatedPrincipal
        processes.createProcess({
          description: 'User reactivated',
        }).complete()
      } catch (error) {
        console.error('Failed to reactivate principal:', error)
        processes.error({ message: 'Failed to reactivate user' })
      }
    }

    async function updateProfile(updates: {
      name?: string
      email?: string
      phone?: string
      picture?: string
      settings?: Record<string, any>
    }) {
      if (!principal.value) return

      try {
        await iamService.updatePrincipal(principal.value.id, updates, principal.value.eTag)
        await refresh()

        processes.createProcess({
          title: 'Profile Updated',
          description: 'Principal profile updated successfully',
        }).complete()
      } catch (error: any) {
        console.error('Failed to update profile:', error)
        processes.error({
          message: error.response?.data?.message || 'Failed to update profile',
        })
        throw error
      }
    }

    async function deletePrincipal() {
      if (!principal.value) return

      const confirmed = await dialogs.confirm(
        `Are you sure you want to delete ${displayName()}? This action cannot be undone.`,
        'Delete User'
      )

      if (!confirmed) return

      try {
        await iamService.deletePrincipal(principalId)
        
        // Broadcast to all PrincipalsView instances
        const iamEvents = useIamEventsStore()
        iamEvents.emitPrincipalsListChanged()
        
        processes.createProcess({
          description: 'User deleted',
        }).complete()
      } catch (error) {
        console.error('Failed to delete principal:', error)
        processes.error({ message: 'Failed to delete user' })
      }
    }

    async function savePermissions() {
      if (!principal.value || pendingPermissions.value === null) return

      try {
        await iamService.updatePrincipalPermissions(
          principal.value.id,
          pendingPermissions.value,
          principal.value.eTag
        )

        await refresh()
        pendingPermissions.value = null

        processes.createProcess({
          description: 'Permissions updated',
        }).complete()
      } catch (error) {
        console.error('Failed to update permissions:', error)
        processes.error({ message: 'Failed to update permissions' })
      }
    }

    async function saveRoles() {
      if (!principal.value || pendingRoleIds.value === null) return

      try {
        await iamService.updatePrincipalRoles(
          principal.value.id,
          pendingRoleIds.value,
          principal.value.eTag
        )

        await refresh()
        pendingRoleIds.value = null

        processes.createProcess({
          description: 'Roles updated',
        }).complete()
      } catch (error) {
        console.error('Failed to update roles:', error)
        processes.error({ message: 'Failed to update roles' })
      }
    }

    function discardPermissions() {
      pendingPermissions.value = null
    }

    function discardRoles() {
      pendingRoleIds.value = null
    }

    return {
      // State
      principal,
      sessions,
      roles,
      isLoading,
      pendingPermissions,
      pendingRoleIds,

      // Permissions
      canWrite,

      // Getters
      displayName,
      roleCount,
      sessionCount,
      hasPermissionsChanges,
      hasRolesChanges,
      hasUnsavedChanges,

      // Services
      dialogs,
      processes,
      iamService,

      // Actions
      refresh,
      suspend,
      reactivate,
      updateProfile,
      deletePrincipal,
      savePermissions,
      saveRoles,
      discardPermissions,
      discardRoles,
    }
  })()
}
